import type { TrasladoLinea } from '../types/traslados'
import type {
  CoberturaTrasladoItem,
  CoberturaTrasladoTransfer,
  DevolucionOrderDetail,
  DevolucionOrderLineItem,
  DevolucionUnitLine,
  DevolucionUnitSelections,
  TipoUnidadEmpaque,
  UnidadEmpaqueItem,
  UnidadesEmpaqueRecibidas
} from '../types/devoluciones'
import {
  ESTADO_DEVOLUCION_CON_FALTANTES,
  ESTADO_DEVOLUCION_PARA_TRANSFERIR
} from '../types/devoluciones'
import {
  FLEXY_PRODUCT_TABLE_WAREHOUSES,
  type ProductTableStockFilterKey
} from './productTableBodegasConfig'

const DEFAULT_DATE_FORMAT = 'es-CO'

export function formatDevolucionDate(date?: string): string {
  if (!date) return ''
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return date
  return parsed.toLocaleString(DEFAULT_DATE_FORMAT)
}

export function getDevolucionCustomerName(order?: DevolucionOrderDetail | null): string {
  const firstName = order?.customer?.first_name?.trim() ?? ''
  const lastName = order?.customer?.last_name?.trim() ?? ''
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || 'Cliente sin nombre'
}

function getDevolucionLineSku(lineItem: DevolucionOrderLineItem): string {
  return String(lineItem.sku || lineItem.product_id || '').trim()
}

export function expandDevolucionLineItems(
  lineItems: DevolucionOrderLineItem[] = []
): DevolucionUnitLine[] {
  return lineItems.flatMap((lineItem, sourceIndex) => {
    const sku = getDevolucionLineSku(lineItem)
    const quantity = Math.max(0, Math.floor(Number(lineItem.quantity) || 0))
    return Array.from({ length: quantity }, (_, unitIndex) => ({
      id: `${sku || 'sku'}-${sourceIndex}-${unitIndex}`,
      sku,
      producto: lineItem.product_name?.trim() || 'Producto sin nombre',
      sourceIndex,
      unitIndex
    }))
  })
}

export function groupUnitsForTraslado(
  units: DevolucionUnitLine[],
  selections: DevolucionUnitSelections,
  targetSelection: DevolucionUnitSelections[string]
): TrasladoLinea[] {
  const grouped = new Map<string, TrasladoLinea>()

  units.forEach((unit) => {
    if (selections[unit.id] !== targetSelection || !unit.sku) return
    const existing = grouped.get(unit.sku)
    if (existing) {
      grouped.set(unit.sku, { ...existing, cantidad: existing.cantidad + 1 })
      return
    }
    grouped.set(unit.sku, {
      sku: unit.sku,
      producto: unit.producto,
      cantidad: 1
    })
  })

  return Array.from(grouped.values())
}

export function getFlexyWarehouseId(filterKey: ProductTableStockFilterKey): number {
  const warehouse = FLEXY_PRODUCT_TABLE_WAREHOUSES.find((item) => item.filterKey === filterKey)
  return warehouse?.idalmacen ?? 0
}

/** Almacén origen fijo para ingreso de devoluciones desde nodo externo. */
export const ID_ALMACEN_NODO_EXTERNO = 0

export function buildOrderLineasForTraslado(
  lineItems: DevolucionOrderLineItem[] = []
): TrasladoLinea[] {
  return lineItems
    .map((lineItem) => ({
      sku: getDevolucionLineSku(lineItem),
      producto: lineItem.product_name?.trim() || 'Producto sin nombre',
      cantidad: Math.max(0, Number(lineItem.quantity) || 0)
    }))
    .filter((linea) => linea.sku && linea.cantidad > 0)
}

export function getCoberturaItemBySku(
  items: CoberturaTrasladoItem[] | undefined,
  sku: string
): CoberturaTrasladoItem | undefined {
  if (!items?.length || !sku) return undefined
  const normalized = sku.trim().toLowerCase()
  return items.find((item) => String(item.sku || '').trim().toLowerCase() === normalized)
}

export type UnidadTrasladoUiStatus = 'aprobado' | 'pendiente' | null

export interface UnidadTrasladoUiInfo {
  status: UnidadTrasladoUiStatus
  consecutivo?: string
  transferId?: number
}

function sumApprovedTransfersCantidad(item: CoberturaTrasladoItem): number {
  if (!Array.isArray(item.approvedTransfers) || item.approvedTransfers.length === 0) {
    return Math.max(0, Math.floor(Number(item.approved) || 0))
  }
  return item.approvedTransfers.reduce(
    (sum, transfer) => sum + Math.max(0, Math.floor(Number(transfer.cantidad) || 0)),
    0
  )
}

/**
 * Marca unidades con el JSON actualizado de cobertura-traslados:
 * - approvedTransfers / approved / fullyApproved → verde (aprobado)
 * - transfers pendientes o pending → amarillo
 * - resto → editable
 */
export function getUnitTrasladoInfo(
  unit: DevolucionUnitLine,
  item?: CoberturaTrasladoItem,
  transfers: CoberturaTrasladoTransfer[] = []
): UnidadTrasladoUiInfo {
  if (!item) return { status: null }

  // Flag explícito del backend: SKU sin traslado → editable
  if (
    item.sin_transferencia === true ||
    item.hasNoTransfer === true ||
    item.tiene_transferencia === false ||
    item.hasAnyTransfer === false
  ) {
    return { status: null }
  }

  const skuKey = String(unit.sku || '').trim().toLowerCase()
  const approvedTransfers = Array.isArray(item.approvedTransfers) ? item.approvedTransfers : []

  if (approvedTransfers.length > 0) {
    let cursor = 0
    for (const transfer of approvedTransfers) {
      const qty = Math.max(0, Math.floor(Number(transfer.cantidad) || 0))
      if (unit.unitIndex >= cursor && unit.unitIndex < cursor + qty) {
        return {
          status: 'aprobado',
          consecutivo: transfer.consecutivo,
          transferId: transfer.id
        }
      }
      cursor += qty
    }
  } else if (item.transferencia_aprobada === true || item.hasApprovedTransfer === true || item.fullyApproved === true) {
    const approved = Math.max(0, Math.floor(Number(item.approved) || 0))
    const expected = Math.max(0, Math.floor(Number(item.expected) || 0))
    const approvedLimit = item.fullyApproved
      ? Math.max(approved, expected)
      : approved

    if (unit.unitIndex < approvedLimit) {
      return { status: 'aprobado' }
    }
  } else {
    const approved = Math.max(0, Math.floor(Number(item.approved) || 0))
    if (unit.unitIndex < approved) {
      return { status: 'aprobado' }
    }
  }

  const approvedCount = sumApprovedTransfersCantidad(item)
  const pendingTransfers = transfers.filter(
    (transfer) => String(transfer.estado || '').toLowerCase() === 'pendiente'
  )

  if (pendingTransfers.length > 0) {
    let cursor = approvedCount
    for (const transfer of pendingTransfers) {
      const lineas = (transfer.lineas || []).filter(
        (linea) => String(linea.sku || '').trim().toLowerCase() === skuKey
      )
      for (const linea of lineas) {
        const qty = Math.max(0, Math.floor(Number(linea.cantidad) || 0))
        if (unit.unitIndex >= cursor && unit.unitIndex < cursor + qty) {
          return {
            status: 'pendiente',
            consecutivo: transfer.consecutivo,
            transferId: transfer.id
          }
        }
        cursor += qty
      }
    }
  } else {
    const pendingReported = Math.max(0, Math.floor(Number(item.pending) || 0))
    const reserved = Math.max(
      0,
      Math.floor(Number(item.reserved ?? approvedCount + pendingReported) || 0)
    )
    const pendingCovered = pendingReported > 0
      ? pendingReported
      : Math.max(0, reserved - approvedCount)

    if (unit.unitIndex >= approvedCount && unit.unitIndex < approvedCount + pendingCovered) {
      return { status: 'pendiente' }
    }
  }

  return { status: null }
}

function getUnitTrasladoStatus(
  unit: DevolucionUnitLine,
  item?: CoberturaTrasladoItem,
  transfers: CoberturaTrasladoTransfer[] = []
): UnidadTrasladoUiStatus {
  return getUnitTrasladoInfo(unit, item, transfers).status
}

function isUnitBlockedForTransfer(
  unit: DevolucionUnitLine,
  item?: CoberturaTrasladoItem,
  transfers: CoberturaTrasladoTransfer[] = []
): boolean {
  return getUnitTrasladoStatus(unit, item, transfers) != null
}

export function getTransferableUnits(
  units: DevolucionUnitLine[],
  coberturaItems: CoberturaTrasladoItem[] = [],
  transfers: CoberturaTrasladoTransfer[] = []
): DevolucionUnitLine[] {
  return units.filter((unit) => {
    const item = getCoberturaItemBySku(coberturaItems, unit.sku)
    return !isUnitBlockedForTransfer(unit, item, transfers)
  })
}

export function everyTransferableUnitHasSelection(
  units: DevolucionUnitLine[],
  selections: DevolucionUnitSelections,
  coberturaItems: CoberturaTrasladoItem[] = [],
  transfers: CoberturaTrasladoTransfer[] = []
): boolean {
  const transferable = getTransferableUnits(units, coberturaItems, transfers)
  return transferable.length > 0 && transferable.every((unit) => Boolean(selections[unit.id]))
}

const TIPOS_UNIDAD_EMPAQUE: TipoUnidadEmpaque[] = ['bolsa', 'caja', 'wacal']

const EMPAQUE_LABELS: Record<TipoUnidadEmpaque, string> = {
  bolsa: 'Bolsa',
  caja: 'Caja',
  wacal: 'Wacal'
}

export function getEmpaqueLabel(tipo: TipoUnidadEmpaque): string {
  return EMPAQUE_LABELS[tipo] || tipo
}

function normalizeUnidadEmpaqueItem(raw: unknown): UnidadEmpaqueItem | null {
  if (!raw || typeof raw !== 'object') return null
  const item = raw as { tipo?: unknown; cantidad?: unknown }
  const tipo = String(item.tipo || '').trim().toLowerCase() as TipoUnidadEmpaque
  if (!TIPOS_UNIDAD_EMPAQUE.includes(tipo)) return null
  const cantidad = Math.max(0, Number(item.cantidad) || 0)
  return { tipo, cantidad }
}

function normalizeUnidadEmpaqueList(raw: unknown): UnidadEmpaqueItem[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map(normalizeUnidadEmpaqueItem)
    .filter((item): item is UnidadEmpaqueItem => Boolean(item))
}

const EMPAQUE_ESPERADA_KEYS = ['despacho', 'envio', 'original', 'empacado', 'empaque'] as const

/** Extrae lista de unidades desde array u objeto anidado. */
function extractUnidadesEmpaqueList(
  value: unknown,
  preferredKey?: 'devolucion' | typeof EMPAQUE_ESPERADA_KEYS[number]
): UnidadEmpaqueItem[] {
  if (Array.isArray(value)) {
    return normalizeUnidadEmpaqueList(value)
  }

  if (!value || typeof value !== 'object') return []

  const nested = value as Record<string, unknown>

  if (preferredKey === 'devolucion') {
    return normalizeUnidadEmpaqueList(nested.devolucion)
  }

  if (preferredKey) {
    const preferred = normalizeUnidadEmpaqueList(nested[preferredKey])
    if (preferred.length > 0) return preferred
  }

  for (const key of EMPAQUE_ESPERADA_KEYS) {
    const list = normalizeUnidadEmpaqueList(nested[key])
    if (list.length > 0) return list
  }

  // Si el objeto solo tiene arrays distintos de devolucion
  for (const [key, entry] of Object.entries(nested)) {
    if (key === 'devolucion') continue
    const list = normalizeUnidadEmpaqueList(entry)
    if (list.length > 0) return list
  }

  return []
}

/** Obtiene unidades empacadas del pedido (raíz o extraDetails). */
export function getOrderUnidadesEmpaque(
  order?: DevolucionOrderDetail | null
): UnidadEmpaqueItem[] {
  const candidates = [
    order?.unidades_empaque,
    order?.extraDetails?.unidades_empaque
  ]

  for (const candidate of candidates) {
    const normalized = extractUnidadesEmpaqueList(candidate)
    if (normalized.length > 0) return normalized
  }

  return []
}

/** Obtiene unidades ya registradas en devolución. */
export function getOrderUnidadesEmpaqueDevolucion(
  order?: DevolucionOrderDetail | null
): UnidadEmpaqueItem[] {
  const candidates = [
    order?.unidades_empaque,
    order?.extraDetails?.unidades_empaque
  ]

  for (const candidate of candidates) {
    const normalized = extractUnidadesEmpaqueList(candidate, 'devolucion')
    if (normalized.length > 0) return normalized
  }

  return []
}

export function buildUnidadesEmpaqueDevolucionPayload(
  recibidas: UnidadesEmpaqueRecibidas
): UnidadEmpaqueItem[] {
  return TIPOS_UNIDAD_EMPAQUE.map((tipo) => ({
    tipo,
    cantidad: Math.max(0, Number(recibidas[tipo]) || 0)
  }))
}

export function createEmptyEmpaqueRecibidas(): UnidadesEmpaqueRecibidas {
  return { bolsa: 0, caja: 0, wacal: 0 }
}

/** true si cada tipo tiene cantidad recibida === cantidad empacada */
function areUnidadesEmpaqueEquivalentes(
  unidadesEmpacadas: UnidadEmpaqueItem[] = [],
  recibidas: UnidadesEmpaqueRecibidas
): boolean {
  const rows = buildEmpaqueVerificationRows(unidadesEmpacadas)
  return rows.every((row) => {
    const recibida = Math.max(0, Number(recibidas[row.tipo]) || 0)
    return recibida === row.esperada
  })
}

export function resolveEstadoDevolucionEmpaque(
  unidadesEmpacadas: UnidadEmpaqueItem[] = [],
  recibidas: UnidadesEmpaqueRecibidas
): typeof ESTADO_DEVOLUCION_PARA_TRANSFERIR | typeof ESTADO_DEVOLUCION_CON_FALTANTES {
  return areUnidadesEmpaqueEquivalentes(unidadesEmpacadas, recibidas)
    ? ESTADO_DEVOLUCION_PARA_TRANSFERIR
    : ESTADO_DEVOLUCION_CON_FALTANTES
}

export function recibidasFromUnidades(
  unidades: UnidadEmpaqueItem[] = []
): UnidadesEmpaqueRecibidas {
  const result = createEmptyEmpaqueRecibidas()
  unidades.forEach((item) => {
    result[item.tipo] = Math.max(0, Number(item.cantidad) || 0)
  })
  return result
}

/** Filas de verificación: siempre bolsa/caja/wacal con cantidad esperada. */
export function buildEmpaqueVerificationRows(
  unidades: UnidadEmpaqueItem[] = []
): Array<{ tipo: TipoUnidadEmpaque; label: string; esperada: number }> {
  const byTipo = new Map(
    unidades.map((item) => [item.tipo, Math.max(0, Number(item.cantidad) || 0)])
  )
  return TIPOS_UNIDAD_EMPAQUE.map((tipo) => ({
    tipo,
    label: getEmpaqueLabel(tipo),
    esperada: byTipo.get(tipo) ?? 0
  }))
}
